/**
 * Vegas lights: full-screen panels of bulbs and LEDs. They share one set of lighting patterns
 * (chase, marquee, ripple, wave, sparkle, spectrum, spiral, checker flip, rain), stepped by the
 * beat, so any panel can run any pattern.
 */
import type { ParamDef } from '../../../shared/params';
import type { VisualizationDef } from '../../../shared/viz';

export const LIGHT_PATTERNS = [
  'Chase',
  'Marquee',
  'Ripple',
  'Wave',
  'Sparkle',
  'Spectrum',
  'Spiral',
  'Checker flip',
  'Rain',
] as const;

const patternParam = (def: number): ParamDef => ({
  id: 'pattern',
  label: 'Pattern',
  type: 'float',
  min: 0,
  max: LIGHT_PATTERNS.length - 1,
  default: def,
  step: 1,
  control: 'select',
  options: [...LIGHT_PATTERNS],
});

const COMMON: ParamDef[] = [
  { id: 'speed', label: 'Speed', type: 'float', min: 0.25, max: 4, default: 1 },
  {
    id: 'colour',
    label: 'Colour',
    type: 'float',
    min: 0,
    max: 2,
    default: 0,
    step: 1,
    control: 'select',
    options: ['Palette across the panel', 'Warm white bulbs', 'Palette by pattern'],
  },
  { id: 'glow', label: 'Glow', type: 'float', min: 0, max: 2, default: 1 },
];

/**
 * GLSL shared by the panels. Params: uP[0] pattern, uP[1] speed, uP[2] colour mode, uP[3] glow;
 * panel-specific params follow from uP[4].
 */
const LIGHTS = /* glsl */ `
float lh(vec2 p) { return hash21(p + 0.37); }
// Unlit bulbs still glow a little, like a real sign between chases.
const float OFF = 0.22;

// How lit one bulb is (0..1). cell: integer coords; pos: position in about −1..1 across the panel;
// idx/count: the bulb's place along the panel's natural path (for chases).
float lightOn(vec2 cell, vec2 pos, float idx, float count) {
  int pat = int(uP[0] + 0.5);
  float b = uBeats * uP[1];
  float beat = floor(b);
  if (pat == 0) {            // Chase: a lit train runs along the path, one step per 16th
    float head = fract(b / 4.0) * count;
    float d = mod(head - idx + count, count);
    return d < count * 0.12 ? 1.0 - d / (count * 0.12) * 0.6 : OFF;
  }
  if (pat == 1) {            // Marquee: every third bulb, stepping on each 8th
    return mod(idx + floor(b * 2.0), 3.0) < 1.0 ? 1.0 : OFF;
  }
  if (pat == 2) {            // Ripple: rings run out from the centre on every beat
    float r = length(pos);
    return pow(0.5 + 0.5 * cos((r * 2.5 - fract(b)) * 6.2831853), 6.0) * (0.5 + 0.5 * pulse()) * (1.0 - OFF) + OFF;
  }
  if (pat == 3) {            // Wave: a travelling sine fills the panel from the bottom
    float h = 0.5 + 0.4 * sin(pos.x * 3.0 - b * 1.5);
    return pos.y * 0.5 + 0.5 < h ? 0.95 : OFF;
  }
  if (pat == 4) {            // Sparkle: random bulbs twinkle, more with the treble
    float h = lh(cell + floor(b * 4.0) * 1.37);
    return h > 0.9 - 0.25 * uTreble ? 1.0 : OFF;
  }
  if (pat == 5) {            // Spectrum: each column is a VU meter
    float x = clamp(pos.x * 0.5 + 0.5, 0.0, 1.0);
    return pos.y * 0.5 + 0.5 < spectrum(x * 0.9) * 1.1 ? 1.0 : OFF;
  }
  if (pat == 6) {            // Spiral: two arms sweep round, one turn per bar
    float a = atan(pos.y, pos.x) / 6.2831853;
    return smoothstep(0.75, 1.0, fract(a * 2.0 + length(pos) * 0.8 - b / 4.0)) * (1.0 - OFF) + OFF;
  }
  if (pat == 7) {            // Checker flip: the board inverts on every beat
    return mod(cell.x + cell.y + beat, 2.0) < 1.0 ? (0.65 + 0.35 * pulse()) : OFF;
  }
  // Rain: drops fall down each column at their own pace
  float spd = 0.5 + lh(vec2(cell.x, 7.0));
  float head = fract(b * 0.25 * spd + lh(vec2(cell.x, 3.0)));
  float y = 1.0 - (pos.y * 0.5 + 0.5);
  float d = head - y;
  return d >= 0.0 && d < 0.25 ? max(1.0 - d * 4.0, OFF) : OFF;
}

vec3 lightColour(vec2 pos, float idx, float count, float on) {
  int mode = int(uP[2] + 0.5);
  if (mode == 1) return mix(vec3(1.0, 0.55, 0.2), vec3(1.0, 0.9, 0.7), on);   // incandescent
  if (mode == 2) return palette(on * 0.6 + uBarPhase * 0.2);
  return palette(fract(idx / max(count, 1.0)) * 0.8 + 0.1);
}

// A round bulb: hot core, glass, and a halo; unlit bulbs still show a dim filament.
vec3 bulb(vec2 d, float radius, vec3 c, float on) {
  float r = length(d) / radius;
  float glass = smoothstep(1.0, 0.85, r);
  float core = exp(-r * r * 3.0);
  float halo = exp(-r * 1.6) * uP[3] * 0.6;
  float lit = on * (0.6 + 0.6 * uLevel);
  return c * (glass * (0.08 + core * lit * 1.5) + halo * lit) + vec3(1.0, 0.7, 0.4) * core * 0.05 * (1.0 - on);
}
`;

export const bulbBoard: VisualizationDef = {
  id: 'bulb-board',
  name: 'Bulb Board',
  description: 'A Vegas sign board of round bulbs running chases, ripples and more on the beat.',
  tags: ['energy:high', 'style:lights'],
  role: 'background',
  params: [
    patternParam(1),
    ...COMMON,
    { id: 'size', label: 'Bulbs across', type: 'float', min: 8, max: 48, default: 24, step: 1 },
  ],
  fragment: /* glsl */ `
${LIGHTS}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  float n = floor(uP[4]);
  float cs = aspect / n;
  vec2 g = (p + vec2(aspect, 1.0) * 0.5) / cs;
  vec2 cell = floor(g);
  vec2 d = fract(g) - 0.5;
  vec2 cells = vec2(n, floor(1.0 / cs));
  vec2 pos = (cell + 0.5) / cells * 2.0 - 1.0;
  // Serpentine path through the rows, like a wired sign.
  float idx = cell.y * cells.x + (mod(cell.y, 2.0) < 1.0 ? cell.x : cells.x - 1.0 - cell.x);
  float count = cells.x * cells.y;
  float on = lightOn(cell, pos, idx, count);
  vec3 c = lightColour(pos, idx, count, on);
  vec3 col = bulb(d, 0.38, c, on) + vec3(0.01, 0.008, 0.012);
  fragColor = vec4(col, 1.0);
}`,
};

export const marqueeFrames: VisualizationDef = {
  id: 'marquee-frames',
  name: 'Marquee Frames',
  description: 'Theatre-marquee frames of bulbs nested inside each other, chasing round the edges.',
  tags: ['energy:high', 'style:lights'],
  role: 'background',
  params: [
    patternParam(1),
    ...COMMON,
    { id: 'frames', label: 'Frames', type: 'float', min: 1, max: 8, default: 5, step: 1 },
  ],
  fragment: /* glsl */ `
${LIGHTS}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  float frames = floor(uP[4]);
  float spacing = 0.5 / (frames + 0.5);
  float step_ = spacing * 0.55;
  vec3 col = vec3(0.01, 0.008, 0.012);
  for (int fk = 1; fk <= 8; fk++) {
    float k = float(fk);
    if (k > frames) break;
    vec2 h = vec2((aspect - 1.0) * 0.5, 0.0) + k * spacing;
    // Closest point on this frame's outline, and how far round the outline it is (clockwise
    // from the top-left corner).
    vec2 c = clamp(p, -h, h);
    vec2 dd = h - abs(c);
    if (dd.x < dd.y) c.x = sign(p.x + 1e-6) * h.x; else c.y = sign(p.y + 1e-6) * h.y;
    float per = 4.0 * (h.x + h.y);
    float t;
    if (abs(c.y - h.y) < 1e-5) t = c.x + h.x;
    else if (abs(c.x - h.x) < 1e-5) t = 2.0 * h.x + (h.y - c.y);
    else if (abs(c.y + h.y) < 1e-5) t = 2.0 * h.x + 2.0 * h.y + (h.x - c.x);
    else t = 4.0 * h.x + 2.0 * h.y + (c.y + h.y);
    float count = floor(per / step_);
    float i0 = floor(t / per * count);
    // The nearest bulb and its neighbours, so corners never clip.
    for (int j = -1; j <= 1; j++) {
      float idx = mod(i0 + float(j) + count, count);
      float tc = (idx + 0.5) / count * per;
      vec2 bp;
      if (tc < 2.0 * h.x) bp = vec2(tc - h.x, h.y);
      else if (tc < 2.0 * h.x + 2.0 * h.y) bp = vec2(h.x, h.y - (tc - 2.0 * h.x));
      else if (tc < 4.0 * h.x + 2.0 * h.y) bp = vec2(h.x - (tc - 2.0 * h.x - 2.0 * h.y), -h.y);
      else bp = vec2(-h.x, -h.y + (tc - 4.0 * h.x - 2.0 * h.y));
      vec2 pos = bp / vec2(aspect * 0.5, 0.5);
      float on = lightOn(vec2(idx, k), pos, idx + k * 7.0, count);
      col += bulb(p - bp, step_ * 0.36, lightColour(pos, idx, count, on), on);
    }
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const starburstSign: VisualizationDef = {
  id: 'starburst-sign',
  name: 'Starburst Sign',
  description: 'A casino starburst: rays of bulbs fanning out from the centre.',
  tags: ['energy:high', 'style:lights'],
  role: 'background',
  params: [
    patternParam(0),
    ...COMMON,
    { id: 'rays', label: 'Rays', type: 'float', min: 8, max: 48, default: 24, step: 1 },
  ],
  fragment: /* glsl */ `
${LIGHTS}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float rays = floor(uP[4]);
  float a = atan(p.y, p.x);
  float seg = 6.2831853 / rays;
  float ray = floor((a + 3.14159265) / seg);
  float ac = (ray + 0.5) * seg - 3.14159265;
  float r = length(p);
  float step_ = 0.045;
  float k = floor(r / step_);
  vec3 col = vec3(0.01, 0.008, 0.012);
  if (k >= 1.0 && k <= 14.0) {
    float rc = (k + 0.5) * step_;
    vec2 bp = rc * vec2(cos(ac), sin(ac));
    float size = min(step_ * 0.34, rc * seg * 0.38);
    vec2 pos = bp / 0.6;
    // Chases run outward along each ray.
    float on = lightOn(vec2(ray, k), pos, k + ray * 0.37, 14.0);
    col += bulb(p - bp, size, lightColour(pos, ray, rays, on), on);
  }
  col += palette(0.9) * exp(-r * 30.0) * (0.6 + pulse());
  fragColor = vec4(col, 1.0);
}`,
};

export const hexLights: VisualizationDef = {
  id: 'hex-lights',
  name: 'Hex Lights',
  description: 'A honeycomb of lit panels, like a club wall, running the light patterns.',
  tags: ['energy:high', 'style:lights'],
  role: 'background',
  params: [
    patternParam(2),
    ...COMMON,
    { id: 'scale', label: 'Cells', type: 'float', min: 4, max: 24, default: 10 },
  ],
  fragment: /* glsl */ `
${LIGHTS}
vec4 hexCoords(vec2 uv) {
  const vec2 s = vec2(1.0, 1.7320508);
  vec4 c = floor(vec4(uv, uv - vec2(0.5, 1.0)) / s.xyxy) + 0.5;
  vec4 h = vec4(uv - c.xy * s, uv - (c.zw + 0.5) * s);
  return dot(h.xy, h.xy) < dot(h.zw, h.zw) ? vec4(h.xy, c.xy) : vec4(h.zw, c.zw + 0.5);
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  vec4 h = hexCoords(p * uP[4]);
  vec2 cell = h.zw;
  vec2 pos = cell / uP[4] / vec2(aspect * 0.5, 0.5);
  float idx = floor(cell.y * 2.0) * 64.0 + cell.x;
  float on = lightOn(floor(cell * 2.0), pos, idx, 64.0 * uP[4]);
  float edge = max(abs(h.x) * 0.866 + abs(h.y) * 0.5, abs(h.y));
  float panel = smoothstep(0.47, 0.43, edge);
  float rim = smoothstep(0.5, 0.44, edge) - panel;
  vec3 c = lightColour(pos, idx, 64.0 * uP[4], on);
  vec3 col = c * panel * (0.04 + on * (0.8 + 0.5 * uLevel)) * (1.0 - 0.6 * edge);
  col += c * rim * on * uP[3] * 0.6 + vec3(0.02) * rim;
  fragColor = vec4(col, 1.0);
}`,
};

export const ledWall: VisualizationDef = {
  id: 'led-wall',
  name: 'LED Wall',
  description: 'A dense wall of square LEDs, like a stadium screen, running the light patterns.',
  tags: ['energy:high', 'style:lights'],
  role: 'background',
  params: [
    patternParam(5),
    ...COMMON,
    { id: 'size', label: 'LEDs across', type: 'float', min: 24, max: 128, default: 64, step: 1 },
  ],
  fragment: /* glsl */ `
${LIGHTS}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  float n = floor(uP[4]);
  float cs = aspect / n;
  vec2 g = (p + vec2(aspect, 1.0) * 0.5) / cs;
  vec2 cell = floor(g);
  vec2 d = abs(fract(g) - 0.5);
  vec2 cells = vec2(n, floor(1.0 / cs));
  vec2 pos = (cell + 0.5) / cells * 2.0 - 1.0;
  float idx = cell.y * cells.x + cell.x;
  float count = cells.x * cells.y;
  float on = lightOn(cell, pos, idx, count);
  float led = smoothstep(0.42, 0.34, max(d.x, d.y));
  vec3 c = lightColour(pos, idx, count, on);
  vec3 col = c * led * (0.03 + on * (0.9 + 0.4 * uLevel)) + c * on * exp(-length(d) * 6.0) * 0.2 * uP[3];
  fragColor = vec4(col, 1.0);
}`,
};

export const discoFloor: VisualizationDef = {
  id: 'disco-floor',
  name: 'Disco Floor',
  description: 'A lit dance floor stretching away into the dark, running the light patterns.',
  tags: ['energy:high', 'style:lights'],
  role: 'background',
  params: [
    patternParam(7),
    ...COMMON,
    { id: 'tiles', label: 'Tiles across', type: 'float', min: 4, max: 16, default: 8, step: 1 },
  ],
  fragment: /* glsl */ `
${LIGHTS}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float horizon = 0.12;
  vec3 col = palette(0.05) * 0.04 * smoothstep(0.5, horizon, p.y);
  if (p.y < horizon - 0.005) {
    // Perspective: screen row → distance across the floor.
    float z = 0.35 / (horizon - p.y);
    vec2 f = vec2(p.x * z, z);
    float n = floor(uP[4]);
    vec2 g = f * n / 1.6;
    vec2 cell = floor(g);
    vec2 d = abs(fract(g) - 0.5);
    vec2 pos = vec2(cell.x / n * 2.0, 1.0 - fract(cell.y / 16.0) * 2.0);
    float idx = cell.y * n + cell.x;
    float on = lightOn(cell, pos, idx, n * 16.0);
    float tile = smoothstep(0.5, 0.44, max(d.x, d.y));
    vec3 c = lightColour(pos, idx, n * 16.0, on);
    float fade = exp(-z * 0.12);
    col = c * tile * (0.05 + on * (0.9 + 0.4 * uLevel)) * fade + c * on * 0.15 * fade * uP[3];
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const LIGHT_VISUALIZATIONS: VisualizationDef[] = [
  bulbBoard,
  marqueeFrames,
  starburstSign,
  hexLights,
  ledWall,
  discoFloor,
];
